// sections/MessengerSection.jsx
// Facebook Messenger channel management: connect a Page with Facebook Login for Business (the "Connect
// Facebook Page" button -> Meta -> messenger-oauth-callback -> back here with ?oauth=ok -> pick a Page),
// or by pasting its id + access token (kept under "Advanced"), disconnect, and pick the default campaign. Modelled on
// WebChatSection.jsx (same SectionCard/fetch pattern); the campaign block below mirrors it closely
// because tenants.messenger_campaign_id behaves exactly like tenants.widget_campaign_id: one slot,
// and the campaign that loses it gets paused by the server.

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  MessageCircle, Loader2, AlertCircle, ShieldAlert, Unplug, Link2, Check, Facebook, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import supabase from '../../../../lib/supabaseClient';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
  if (!session?.access_token) throw new Error('No valid session. Please log in again.');
  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, ...options.headers },
  });
  let body = null;
  try { body = await response.json(); } catch { /* no body */ }
  if (!response.ok) throw new Error(body?.error || `Request failed (${response.status})`);
  return body;
};

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center space-x-3 mb-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

export default function MessengerSection() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // GET /messenger/page response

  const [pageId, setPageId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connectSuccess, setConnectSuccess] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Facebook Login flow
  const [fbStarting, setFbStarting] = useState(false);
  const [fbError, setFbError] = useState('');
  const [oauthPages, setOauthPages] = useState(null); // null = no picker; array = the Pages Facebook returned
  const [pickedPageId, setPickedPageId] = useState('');
  const [selecting, setSelecting] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [origCampaignId, setOrigCampaignId] = useState('');
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [campaignSuccess, setCampaignSuccess] = useState(false);
  const [campaignError, setCampaignError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/messenger/page');
      setStatus(data);
      setCampaignId(data.campaign_id || '');
      setOrigCampaignId(data.campaign_id || '');
    } catch (err) {
      console.error('Failed to fetch Messenger status:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    if (!user?.tenant_id) return;
    try {
      // Every non-archived campaign, same as the web-chat picker: the server refuses an archived one too.
      const { data, error } = await supabase
        .from('campaigns').select('id, name, ai_on').eq('tenant_id', user.tenant_id).not('archived', 'is', true).order('name');
      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err.message);
    }
  }, [user?.tenant_id]);

  useEffect(() => { fetchStatus(); fetchCampaigns(); }, [fetchStatus, fetchCampaigns]);

  // Coming back from Meta: messenger-oauth-callback redirects here with ?oauth=ok|denied|error[&reason=...].
  // Read it once, then strip it from the address bar so a refresh does not replay it.
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    const outcome = params.get('oauth');
    if (!outcome) return;
    const reason = params.get('reason');
    try { window.history.replaceState({}, '', `${window.location.pathname}?section=messenger`); } catch { /* ignore */ }
    if (outcome === 'ok') {
      makeAuthenticatedRequest('/messenger/oauth/pages')
        .then((r) => {
          const pages = r?.pages || [];
          if (pages.length === 0) setFbError('Facebook did not return any Pages for this login. Start again and make sure you select a Page.');
          else { setOauthPages(pages); setPickedPageId(pages.length === 1 ? pages[0].id : ''); }
        })
        .catch((err) => setFbError(err.message || 'Could not load your Pages.'));
    } else if (outcome === 'denied') {
      setFbError('Facebook login was cancelled, so nothing was connected.');
    } else {
      const why = {
        invalid_state: 'That login link expired or was already used. Click Connect Facebook Page to start again.',
        exchange_failed: 'Facebook did not accept the login. Try again, and if it keeps failing use Advanced below.',
        accounts_failed: 'Facebook would not list your Pages. Try again.',
        no_pages: 'Facebook did not return any Pages for this login. Start again and make sure you select a Page.',
      }[reason];
      setFbError(why || 'Something went wrong connecting to Facebook. Try again.');
    }
  }, []);

  const handleFacebookConnect = async () => {
    setFbStarting(true); setFbError('');
    try {
      const { url } = await makeAuthenticatedRequest('/messenger/oauth/start');
      window.location.href = url;
    } catch (err) {
      setFbError(err.message || 'Could not start the Facebook login.');
      setFbStarting(false);
    }
  };

  const handleSelectPage = async () => {
    if (!pickedPageId) return;
    setSelecting(true); setFbError('');
    try {
      await makeAuthenticatedRequest('/messenger/oauth/select', { method: 'POST', body: JSON.stringify({ page_id: pickedPageId }) });
      setOauthPages(null); setPickedPageId('');
      setConnectSuccess(true);
      setTimeout(() => setConnectSuccess(false), 3000);
      await fetchStatus();
    } catch (err) {
      setFbError(err.message || 'Could not connect that Page.');
    } finally {
      setSelecting(false);
    }
  };

  const handleCancelPicker = async () => {
    setOauthPages(null); setPickedPageId('');
    try { await makeAuthenticatedRequest('/messenger/oauth/cancel', { method: 'POST' }); } catch { /* the sweep removes it */ }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true); setConnectError(''); setConnectSuccess(false);
    try {
      await makeAuthenticatedRequest('/messenger/page/connect', {
        method: 'POST',
        body: JSON.stringify({ page_id: pageId.trim(), access_token: accessToken.trim() }),
      });
      setAccessToken(''); // never leave a token sitting in a form field after a successful save
      setPageId('');
      setConnectSuccess(true);
      setTimeout(() => setConnectSuccess(false), 3000);
      await fetchStatus();
    } catch (err) {
      setConnectError(err.message || 'Could not connect the Page.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectConfirm) { setDisconnectConfirm(true); return; }
    setDisconnecting(true);
    try {
      await makeAuthenticatedRequest('/messenger/page/disconnect', { method: 'POST' });
      setDisconnectConfirm(false);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to disconnect Messenger Page:', err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveCampaign = async () => {
    setSavingCampaign(true); setCampaignSuccess(false); setCampaignError('');
    try {
      const result = await makeAuthenticatedRequest('/messenger/campaign', {
        method: 'PATCH', body: JSON.stringify({ campaign_id: campaignId || null }),
      });
      const paused = result?.paused_campaign;
      if (paused?.id) setCampaigns((prev) => prev.map((c) => (c.id === paused.id ? { ...c, ai_on: false } : c)));
      setOrigCampaignId(campaignId);
      setCampaignSuccess(true);
      setTimeout(() => setCampaignSuccess(false), 3000);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to save Messenger campaign:', err.message);
      setCampaignError(err.message || 'Could not save the campaign.');
    } finally {
      setSavingCampaign(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  // One campaign, one channel: hide campaigns already serving Web Chat or Voice (the server refuses them too).
  // The campaign Messenger already uses always stays in the list so the current choice is visible.
  const takenElsewhere = new Set(status?.other_channel_campaign_ids || []);
  const pickableCampaigns = campaigns.filter((c) => c.id === origCampaignId || !takenElsewhere.has(c.id));
  const connectedCampaign = campaigns.find((c) => c.id === campaignId);
  const campaignDirty = campaignId !== origCampaignId;

  // One card, three states. needsAttention = a Page row exists but is not 'active' (token_invalid: Meta rejected
  // the saved token, error 190, which is how a revoked permission shows up; disabled: switched off).
  const isConnected = !!status?.connected;
  const needsAttention = isConnected && status.status !== 'active';
  const pageLabel = status?.page_name || status?.page_id;

  const fbErrorBox = fbError ? (
    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-red-700">{fbError}</p>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <SectionCard
        icon={MessageCircle}
        iconColor="from-blue-500 to-indigo-600"
        title="Facebook Page"
        subtitle={isConnected ? null : 'Sign in with Facebook and choose the Page that should answer Messenger messages'}
      >
        {oauthPages ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">Choose the Page to connect:</p>
            <div className="space-y-2">
              {oauthPages.map((p) => (
                <label key={p.id}
                  className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${pickedPageId === p.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="fb-page" value={p.id} checked={pickedPageId === p.id} onChange={() => setPickedPageId(p.id)} />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{p.name || 'Unnamed Page'}</p>
                    <p className="text-xs text-slate-500 font-mono">{p.id}</p>
                  </div>
                </label>
              ))}
            </div>
            {fbErrorBox}
            <div className="flex items-center gap-2">
              <motion.button onClick={handleSelectPage} disabled={!pickedPageId || selecting} whileTap={{ scale: 0.98 }}
                className={`px-6 py-2.5 rounded-xl text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 flex items-center space-x-2 ${(!pickedPageId || selecting) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {selecting ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Connecting…</span></>) : (<span>Connect this Page</span>)}
              </motion.button>
              <button onClick={handleCancelPicker} disabled={selecting}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 bg-white border border-slate-300 hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        ) : isConnected ? (
          <div className="space-y-4">
            {needsAttention && (
              <div className="flex items-start justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900">
                    {status.status === 'token_invalid'
                      ? `Facebook rejected the saved access${status.last_error ? ` (${status.last_error})` : ''}, so Messenger cannot reply right now.`
                      : 'This Page is switched off, so Messenger cannot reply right now.'}
                    {' '}Reconnect to fix it.
                  </p>
                </div>
                <motion.button onClick={handleFacebookConnect} disabled={fbStarting} whileTap={{ scale: 0.97 }}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors ${fbStarting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {fbStarting ? 'Opening Facebook…' : 'Reconnect'}
                </motion.button>
              </div>
            )}
            {fbErrorBox}
            <div className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-xl">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{pageLabel}</p>
                <p className="text-xs text-slate-500 font-mono">{status.page_id}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {disconnectConfirm && (
                  <button onClick={() => setDisconnectConfirm(false)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                )}
                {!disconnectConfirm && (
                  <motion.button onClick={handleFacebookConnect} disabled={fbStarting} whileTap={{ scale: 0.97 }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors ${fbStarting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {fbStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Facebook className="w-3.5 h-3.5" />}
                    {fbStarting ? 'Opening Facebook…' : 'Switch Page'}
                  </motion.button>
                )}
                <motion.button onClick={handleDisconnect} disabled={disconnecting} whileTap={{ scale: 0.97 }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                    ${disconnectConfirm ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white text-red-600 border border-red-300 hover:bg-red-50'}
                    ${disconnecting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                  {disconnectConfirm ? 'Yes, disconnect' : 'Disconnect'}
                </motion.button>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              {disconnectConfirm ? 'Confirm: Messenger will stop answering immediately.' : 'Disconnect stops the AI from reading or answering Messenger messages.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {fbErrorBox}
            <motion.button onClick={handleFacebookConnect} disabled={fbStarting} whileTap={{ scale: 0.98 }}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#1877F2] hover:bg-[#166fe0] flex items-center space-x-2 shadow-sm ${fbStarting ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {fbStarting ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Opening Facebook…</span></>)
                : (<><Facebook className="w-4 h-4" /><span>Connect Facebook Page</span></>)}
            </motion.button>
            <p className="text-xs text-slate-400">
              You will be sent to Facebook to approve access, then brought back here to choose a Page.
            </p>
          </div>
        )}
      </SectionCard>

      <details className="group">
        <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-500 hover:text-slate-700 list-none">
          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" /> Advanced: paste a Page ID and token instead
        </summary>
        <div className="mt-3">
      <SectionCard
        icon={Link2}
        iconColor="from-slate-600 to-slate-700"
        title={status?.connected ? 'Reconnect / rotate token' : 'Paste a token'}
        subtitle="Paste the Page ID and a Page access token from Meta's Graph API Explorer or App Dashboard"
      >
        <form onSubmit={handleConnect} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Page ID</label>
            <input
              type="text" value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="104500123456789"
              required pattern="\d{5,30}" title="The numeric Facebook Page ID, not a username or URL"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-mono bg-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Page access token</label>
            <input
              type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)}
              placeholder="EAAG..." required autoComplete="off"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-mono bg-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1.5">
              Checked against Meta before it is saved, and stored encrypted. It is never shown again after saving.
            </p>
          </div>
          {connectError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{connectError}</p>
            </div>
          )}
          <motion.button type="submit" disabled={connecting} whileTap={{ scale: 0.98 }}
            className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow
              ${connectSuccess ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}
              ${connecting ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {connecting ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Connecting…</span></>)
              : connectSuccess ? (<><Check className="w-4 h-4" /><span>Connected</span></>)
              : (<span>{status?.connected ? 'Save new token' : 'Connect Page'}</span>)}
          </motion.button>
        </form>
      </SectionCard>
        </div>
      </details>

      {status?.connected && (
        <SectionCard
          icon={ShieldAlert}
          iconColor="from-cyan-500 to-blue-600"
          title="Default campaign"
          subtitle="Which campaign answers your Messenger leads"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Default campaign for Messenger leads</label>
            <select
              value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setCampaignError(''); }}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">No campaign (unassigned)</option>
              {pickableCampaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.ai_on === false ? ' (paused)' : ''}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1.5">
              New Messenger leads are enrolled in this campaign automatically. Messenger answers only while this
              campaign is live: with no campaign, or a paused or archived one, it goes quiet.
            </p>
            {campaignId && connectedCampaign?.ai_on === false && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                This campaign is paused, so Messenger will not answer until you turn it on.
              </p>
            )}
            {origCampaignId && campaignDirty && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                <strong className="font-semibold">{campaigns.find((c) => c.id === origCampaignId)?.name || 'The current Messenger campaign'}</strong> will
                be paused when you save. It will not text anyone until you turn it on again and it passes the SMS checks.
                {!campaignId && ' With no campaign picked, Messenger goes quiet.'}
              </p>
            )}
            {campaignError && (
              <div className="mt-2 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{campaignError}</p>
              </div>
            )}
            {campaignDirty && (
              <div className="flex items-center justify-end pt-3">
                <motion.button onClick={handleSaveCampaign} disabled={savingCampaign} whileTap={{ scale: 0.98 }}
                  className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow
                    ${campaignSuccess ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}
                    ${savingCampaign ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {savingCampaign ? (<><Loader2 className="w-4 h-4 animate-spin" /><span>Saving…</span></>)
                    : campaignSuccess ? (<><Check className="w-4 h-4" /><span>Saved</span></>)
                    : (<span>Save Changes</span>)}
                </motion.button>
              </div>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
